import React, { useState, useEffect } from 'react'
import { catalogAPI } from '../../services/api'
import { injectFontFace, injectFontFamilyVariants } from '../../utils/fontLoader'

const FontPreviewPage = () => {
  const [fonts, setFonts] = useState([])
  const [loading, setLoading] = useState(true)
  const [fontSize, setFontSize] = useState(25)
  const [previewText, setPreviewText] = useState('Hello World')

  useEffect(() => {
    loadFonts()
  }, [])

  const loadFonts = async () => {
    try {
      setLoading(true)
      const response = await catalogAPI.getFonts()
      if (response.data?.success && response.data.data?.fonts) {
        // Convert CDN URLs to proxy URLs for all fonts
        const fontsList = response.data.data.fonts.map(font => {
          const filename = font.file || font.filename
          // Convert CDN URL to proxy URL to avoid CORS issues
          let url = font.url
          if (url && url.startsWith('https://editor.blr1.cdn.digitaloceanspaces.com/fonts/')) {
            url = `/api/v1/catalog/fonts/proxy/${encodeURIComponent(filename)}`
          } else if (filename && !url) {
            url = `/api/v1/catalog/fonts/proxy/${encodeURIComponent(filename)}`
          }
          return { ...font, url }
        })
        
        // Extract unique font families for display
        const familyMap = new Map()
        fontsList.forEach(font => {
          if (font.family && !familyMap.has(font.family)) {
            familyMap.set(font.family, font)
          }
        })
        const uniqueFonts = Array.from(familyMap.values()).sort((a, b) => 
          a.family.localeCompare(b.family)
        )
        setFonts(uniqueFonts)
        
        // Inject all variants for each font family to support all weights/styles
        // This includes ALL fonts from A to Z
        uniqueFonts.forEach(font => {
          try {
            injectFontFamilyVariants(fontsList, font.family, 20)
          } catch (err) {
            console.warn(`Failed to inject font variants for ${font.family}:`, err)
          }
        })
      }
    } catch (error) {
      console.error('Failed to load fonts:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    // Font loading happens automatically, so this just updates the preview
  }

  // Organize fonts into columns (3 columns)
  const getColumns = () => {
    const columns = [[], [], []]
    fonts.forEach((font, index) => {
      columns[index % 3].push(font)
    })
    return columns
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading fonts...</p>
        </div>
      </div>
    )
  }

  const columns = getColumns()

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* Control Panel */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6 max-w-4xl mx-auto">
        <form onSubmit={handleSubmit} className="flex items-end gap-6">
          <div className="flex-1">
            <label htmlFor="fontSize" className="block text-sm font-medium text-gray-700 mb-2">
              Font Size
            </label>
            <input
              type="number"
              id="fontSize"
              value={fontSize}
              onChange={(e) => setFontSize(parseInt(e.target.value) || 25)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              min="1"
              max="200"
            />
          </div>
          
          <div className="flex-1">
            <label htmlFor="previewText" className="block text-sm font-medium text-gray-700 mb-2">
              Font Text
            </label>
            <input
              type="text"
              id="previewText"
              value={previewText}
              onChange={(e) => setPreviewText(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              placeholder="Hello World"
            />
          </div>
          
          <button
            type="submit"
            className="px-8 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-200 shadow-sm uppercase"
          >
            SUBMIT
          </button>
        </form>
      </div>

      {/* Font Display Area */}
      <div className="bg-gray-100 rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-7xl mx-auto">
          {columns.map((column, colIndex) => (
            <div key={colIndex} className="space-y-3">
              {column.map((font, index) => (
                <div
                  key={`${font.id}-${index}`}
                  className="bg-white rounded p-4 shadow-sm hover:shadow transition-shadow duration-200"
                >
                  {/* Preview Text */}
                  <div
                    className="mb-2 break-words"
                    style={{
                      fontFamily: `"${font.family}", sans-serif, Arial`,
                      fontSize: `${fontSize}px`,
                      lineHeight: '1.3',
                      color: '#000',
                      fontWeight: 'normal',
                      fontStyle: 'normal',
                      minHeight: `${fontSize * 1.5}px`
                    }}
                  >
                    {previewText || 'Hello World'}
                  </div>
                  
                  {/* Font Name */}
                  <div className="text-xs text-gray-600 font-sans leading-tight">
                    {font.family.length > 30 ? `${font.family.substring(0, 30)}...` : font.family}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

    </div>
  )
}

export default FontPreviewPage
